"use client"

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
    Activity,
    Bot,
    ChevronLeft,
    ChevronRight,
    Radar,
    Shield,
    Sparkles,
} from "lucide-react";
import { Products } from "@/types/productsTypes";

const highlightIcons = {
    shield: Shield,
    bot: Bot,
    radar: Radar,
    activity: Activity,
    sparkles: Sparkles,
} as const;

const ctaIconMap = {
    shield: Shield,
    bot: Bot,
    radar: Radar,
    activity: Activity,
    sparkles: Sparkles,
} as const;

export function ClaireAIHero({ section }: { section: Products["atlasai"]["hero"] }) {
    const highlights = section.highlights ?? [];
    // const buttons = [section.primaryButton, section.secondaryButton].filter(
    //     (button): button is NonNullable<typeof section.primaryButton> => Boolean(button),
    // );
    const buttons = [section.primaryButton].filter(
        (button): button is NonNullable<typeof section.primaryButton> => Boolean(button),
    );

    const carouselImages = useMemo(() => {
        if (section.carouselImages && section.carouselImages.length > 0) {
            return section.carouselImages;
        }

        return section.image ? [section.image] : [];
    }, [section.carouselImages, section.image]);

    const [currentIndex, setCurrentIndex] = useState(0);
    const totalSlides = carouselImages.length;

    useEffect(() => {
        setCurrentIndex(0);
    }, [totalSlides]);

    useEffect(() => {
        if (totalSlides <= 1) {
            return;
        }

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % totalSlides);
        }, 5000);

        return () => clearInterval(interval);
    }, [totalSlides]);

    const goToSlide = (index: number) => {
        if (totalSlides === 0) {
            return;
        }

        const normalizedIndex = ((index % totalSlides) + totalSlides) % totalSlides;
        setCurrentIndex(normalizedIndex);
    };

    return (
        <section className="section-primary">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="space-y-6"
                >
                    <span className="badge-size section-badge w-fit">
                        {section.badge}
                    </span>
                    <h1 className="title-size font-bold text-bg">
                        {section.title}
                    </h1>
                    <p className="text-size text-muted-foreground max-w-xl">
                        {section.description}
                    </p>

                    <div className="grid sm:grid-cols-2 gap-4">
                        {highlights.map((item, index) => {
                            const Icon = highlightIcons[item.icon as keyof typeof highlightIcons] || Shield;
                            return (
                                <motion.div
                                    key={item.title}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    className="rounded-2xl border bg-white p-5 shadow-sm"
                                >
                                    <div className="iconBudg size-10 mb-3">
                                        <Icon className="size-5" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-bg">
                                        {item.title}
                                    </h3>
                                    <p className="text-sm text-muted-foreground">
                                        {item.description}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        {buttons.map((button, idx) => {
                            const Icon = button.icon
                                ? ctaIconMap[button.icon as keyof typeof ctaIconMap]
                                : undefined;
                            return (
                                <Link key={button.text} href={button.link} target={button.target}>
                                    <Button
                                        variant={button.variant || (idx === 0 ? "primary" : "outline")}
                                        size="xl"
                                        className="w-full sm:w-auto"
                                    >
                                        {Icon && <Icon className="size-5 mr-2" />}
                                        {button.text}
                                    </Button>
                                </Link>
                            );
                        })}
                    </div>
                </motion.div>

                {totalSlides > 0 && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="relative"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-red-200 via-orange-200 to-amber-200 blur-3xl opacity-40" />
                        <div className="relative border rounded-3xl p-6 bg-white shadow-xl">
                            <Link
                                href="https://clairesecurity.ai"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/40 rounded-2xl"
                                aria-label="Visit ClaireSecurity"
                            >
                                <Image
                                    key={carouselImages[currentIndex]}
                                    src={carouselImages[currentIndex]}
                                    alt={section.title}
                                    width={960}
                                    height={720}
                                    className="w-full h-auto rounded-2xl transition-opacity duration-500"
                                    priority
                                />
                            </Link>

                            {totalSlides > 1 && (
                                <>
                                    <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4">
                                        <button
                                            type="button"
                                            onClick={() => goToSlide(currentIndex - 1)}
                                            className="inline-flex size-10 items-center justify-center rounded-full bg-white/90 shadow-md transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                            aria-label="Previous image"
                                        >
                                            <ChevronLeft className="size-5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => goToSlide(currentIndex + 1)}
                                            className="inline-flex size-10 items-center justify-center rounded-full bg-white/90 shadow-md transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                            aria-label="Next image"
                                        >
                                            <ChevronRight className="size-5" />
                                        </button>
                                    </div>

                                    <div className="absolute -bottom-4 left-1/2 flex -translate-x-1/2 gap-2 rounded-full bg-white/90 px-3 py-2 shadow-md">
                                        {carouselImages.map((_, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                onClick={() => goToSlide(index)}
                                                className={`size-2.5 rounded-full transition ${index === currentIndex ? "bg-primary" : "bg-muted"}`}
                                                aria-label={`Go to image ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </div>
        </section>
    );
}
